import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { changeAdminRole } from "@/services/role-access.service";
import ChangeRoleDialog from "./change-role-dialog";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { toggleUser, deleteAdminUser } from "@/services/users.service";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { formatName } from "@/utils/format-name";
import { showToast } from "@/redux/actions/toast-actions";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

export function AdminActionsPopover({ admin }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [changeRoleOpen, setChangeRoleOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const isActive = admin?.active;
  const adminName =
    admin?.firstName && admin?.surname
      ? formatName(admin.firstName, admin.surname)
      : "this admin";

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const toggleMutation = useMutation({
    mutationFn: () => toggleUser({ userId: admin.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminUser(admin.id),
    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Admin deleted successfully", "success"),
      );
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["adminStats"] });
    },
  });

  const handleManageProfile = () => {
    handleClose();
    navigate(`/users/admins/profile/${admin.id}`);
  };

  const handleDeactivateClick = () => {
    handleClose();
    setDeactivateOpen(true);
  };

  const handleDialogClose = () => {
    setDeactivateOpen(false);
    toggleMutation.reset();
  };

  const handleChangeRole = () => {
    handleClose();
    setChangeRoleOpen(true);
  };

  const handleDeleteClick = () => {
    handleClose();
    setDeleteOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (deleteMutation.isPending) return;
    setDeleteOpen(false);
    deleteMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <PopoverContent>
          <StyledMenuItem onClick={handleManageProfile}>
            Profile
          </StyledMenuItem>
          <StyledMenuItem onClick={handleChangeRole}>
            Change Role
          </StyledMenuItem>
          <Divider sx={{ my: 0.5 }} />
          {isActive ? (
            <StyledMenuItem
              onClick={handleDeactivateClick}
              sx={{ color: "error.main" }}
            >
              Deactivate User
            </StyledMenuItem>
          ) : (
            <StyledMenuItem
              onClick={handleDeactivateClick}
              sx={{ color: "text.primary" }}
            >
              Activate User
            </StyledMenuItem>
          )}
          <StyledMenuItem
            onClick={handleDeleteClick}
            sx={{ color: "error.main" }}
          >
            Delete Admin
          </StyledMenuItem>
        </PopoverContent>
      </Popover>

      <ChangeRoleDialog
        open={changeRoleOpen}
        onClose={() => setChangeRoleOpen(false)}
        admin={admin}
        mutationFn={changeAdminRole}
      />

      <CustomConfirmDialog
        open={deactivateOpen}
        onClose={handleDialogClose}
        title={isActive ? "Deactivate User" : "Activate User"}
        description={
          isActive
            ? `Are you sure you want to deactivate ${adminName}? They will lose access immediately.`
            : `Are you sure you want to activate ${adminName}?`
        }
        confirmLabel={isActive ? "Deactivate" : "Activate"}
        variant={isActive ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() => toggleMutation.mutate()}
      />

      <CustomConfirmDialog
        open={deleteOpen}
        onClose={handleDeleteDialogClose}
        title="Delete Admin"
        description={`Are you sure you want to delete ${adminName}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        mutation={deleteMutation}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  );
}
